/**
 * Phase 266c — Household Invite Link via WhatsApp.
 *
 * One-tap CTA that opens WhatsApp Web / native app with a pre-filled,
 * brand-respecting share message + a deep link to the academy's public
 * wall (`/academy/{slug}?install=1`).  When relatives tap the link on
 * mobile, the existing Phase 264c FirstLoginPwaModal fires (because
 * `install=1` is in the URL → we read it client-side and force the
 * modal regardless of localStorage flag).
 *
 * Designed for honesty + real URLs:
 *   • Uses the REAL `slug` field from the auth payload — not
 *     `btoa(name)` which produces unreadable URLs and isn't routeable.
 *   • Falls back gracefully when no slug present (silent — never breaks
 *     the parent layout).
 *   • Single button, single CTA — no decision paralysis.
 *
 * Telemetry: emits `pwa_install_invite_clicked` via the existing PWA
 * telemetry pipe so PostHog can attribute household-installed PWAs to
 * the household-share surface specifically.
 */
import { useCallback, useState } from 'react';
import { Share2, MessageSquare, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { trackPwa, personaForRole } from '@/lib/pwaTelemetry';

const SOURCE = 'household_invite';

function buildInviteUrl(slug) {
  if (!slug) return null;
  // Production hostname is fixed; for preview we read REACT_APP_BACKEND_URL
  // host so the link still resolves to a routable page in QA.
  // We always prefer https://out-play.in for shareable links so parents
  // who click from WhatsApp land on the canonical brand domain.
  return `https://out-play.in/academy/${encodeURIComponent(slug)}?install=1`;
}

function buildShareText({ academyName, studentName, inviteUrl }) {
  const subject = academyName ? `${academyName}` : 'our sports academy';
  const lead = studentName ? `Hey! ${studentName} trains with ${subject}.` : `Hey! We use ${subject} for training.`;
  return (
    `${lead}\n\n` +
    `The official Out-Play app shows live attendance, fee status, progress cards and match clips. ` +
    `Tap the link and choose "Add to Home Screen" — no app-store install:\n\n` +
    `${inviteUrl}`
  );
}

export default function HouseholdInviteLink({ academyName, studentName }) {
  const { user } = useAuth();
  const [sent, setSent] = useState(false);

  const slug = user?.academy_slug;
  const inviteUrl = buildInviteUrl(slug);

  const handleClick = useCallback(() => {
    if (!inviteUrl) return;
    const text = buildShareText({ academyName, studentName, inviteUrl });
    const target = `https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`;
    trackPwa('pwa_install_invite_clicked', {
      source: SOURCE,
      persona: personaForRole(user?.role),
      role: user?.role,
    });
    // window.open inside a click handler — safe from popup blockers.
    window.open(target, '_blank');
    setSent(true);
    setTimeout(() => setSent(false), 3000);
  }, [academyName, studentName, inviteUrl, user]);

  // Self-hide when there's no slug → no link to share. Don't render a
  // broken button.
  if (!inviteUrl) return null;

  return (
    <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 dark:border-emerald-900/50 dark:bg-emerald-950/30 p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3" data-testid="household-invite-card">
      <div className="flex items-start gap-3 min-w-0">
        <div className="h-9 w-9 rounded-xl bg-white dark:bg-slate-900 border border-emerald-200 dark:border-emerald-900/60 flex items-center justify-center text-emerald-700 dark:text-emerald-400 shrink-0">
          <Share2 className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-bold text-slate-900 dark:text-slate-100" data-testid="household-invite-title">
            Send the app to a parent / relative
          </p>
          <p className="text-[11px] text-slate-600 dark:text-slate-400 leading-snug mt-0.5">
            Forward a one-tap install link via WhatsApp so the whole household stays in sync.
          </p>
        </div>
      </div>
      <Button
        type="button"
        onClick={handleClick}
        size="sm"
        className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5 font-semibold shrink-0"
        data-testid="household-invite-cta"
        disabled={sent}
      >
        {sent ? <Check className="h-3.5 w-3.5" /> : <MessageSquare className="h-3.5 w-3.5" />}
        {sent ? 'WhatsApp opened' : 'Send via WhatsApp'}
      </Button>
    </div>
  );
}
